<style lang="scss" scopped>
.attribute {
  font-weight: 300;
  color: rgb(79, 79, 79);
}
</style>
<template>
  <div>
    <v-simple-table>
      <thead>
        <tr>
          <th class="text-center" width="80px">
            <v-icon>mdi-check</v-icon>
          </th>
          <th>{{ $capitalize($tc("model.productbudget_coverage")) }}</th>
          <th>{{ $capitalize($tc("model.productbudget_advertisement")) }}</th>
          <th class="text-right" width="150px">{{ $capitalize($tc("model.productbudget_price")) }}</th>
          <th width="100px"></th>
        </tr>
      </thead>
      <tbody>
        <tr v-if="budgets.length == 0" key="empty">
          <td class="grey--text text-center" colspan="5">
            {{ $capitalize($tc("model.productbudget_no_budget")) }}
          </td>
        </tr>
        <tr v-for="item in budgets" :key="item.id" class="budget-row">
          <td class="text-center">
            <v-simple-checkbox
              @click="handleSelectProductBudget(!item.selected, item)"
              :value="item.selected"
              :disabled="
                !$store.getters['auth/getScope'](
                  'leads.damages.budgets.approve'
                )
              "
            ></v-simple-checkbox>
          </td>
          <td>{{ item.coverage.type_description }}</td>
          <td>
            <v-chip
              v-if="item.store_name && item.store_sale_url"
              :href="item.store_sale_url"
              target="_blank"
              link
            >
              <v-icon small left>mdi-open-in-new</v-icon>
              {{ item.store_name }}
            </v-chip>
            <span v-else class="grey--text">{{ $capitalize($tc("model.productbudget_no")) }}</span>
          </td>
          <td class="text-right" :class="{ 'grey--text': !item.selected }">
            {{ formatEuro(item.price) }}
          </td>
          <td>
            <v-btn @click="handleEditBudget(item)" icon small>
              <v-icon small>mdi-pencil</v-icon>
            </v-btn>
            <v-btn @click="handleConfirmBudgetExclusion(item)" icon small>
              <v-icon small>mdi-delete</v-icon>
            </v-btn>
          </td>
        </tr>
      </tbody>
      <tfoot>
        <tr key="total">
          <td colspan="3" class="text-right">{{ $capitalize($tc("model.productbudget_approved_total")) }}</td>
          <td class="text-right">{{ formatEuro(calcTotalBudget()) }}</td>
          <td></td>
        </tr>
      </tfoot>
    </v-simple-table>
    <v-divider></v-divider>
    <v-row class="mt-1">
      <v-col>
        <v-chip @click="handleOpenBudgetDialog()">
          <v-icon left>mdi-plus</v-icon>{{ $capitalize($tc("model.productbudget_register_budget")) }}
        </v-chip>
      </v-col>
    </v-row>
    <v-dialog v-model="budget_dialog" persistent max-width="1000">
      <v-card>
        <v-form @submit.prevent="handleSubmit()">
          <ValidationObserver ref="form" v-slot="{ invalid }">
            <v-card-title>
              {{ $capitalize($tc("model.productbudget_product_budget")) }}
              <v-spacer></v-spacer>
              <v-btn @click="handleCloseBudgetDialog()" icon>
                <v-icon>mdi-close</v-icon>
              </v-btn>
            </v-card-title>
            <v-divider></v-divider>
            <v-card-text>
              <ValidationProvider
                name="policy_coverage"
                rules="required"
                v-slot="{ errors }"
              >
                <v-select
                  :label="$capitalize($tc('model.productbudget_coverage'))"
                  :items="coverages"
                  name="policy_coverage"
                  item-value="id"
                  item-text="type_description"
                  clearable
                  :error-messages="errors"
                  v-model="budget_form.policy_coverage_id"
                  :disabled="submitting"
                ></v-select>
              </ValidationProvider>
              <v-card elevation="3" class="my-5">
                <v-card-title>{{ $capitalize($tc("model.productbudget_digital_quote")) }}</v-card-title>
                <v-card-subtitle>
                  <a @click="marketplace_dialog = true">
                    {{ $capitalize($tc("model.productbudget_integrated_marketplace")) }}
                  </a>
                </v-card-subtitle>
                <v-card-text>
                  <v-row>
                    <v-col cols="3">
                      <v-combobox
                        :items="getMarketplaces()"
                        item-text="name"
                        item-value="name"
                        :label="$capitalize($tc('model.productbudget_e-commerce'))"
                        prepend-inner-icon="mdi-store"
                        v-model="budget_form.store_name"
                        :disabled="submitting"
                        :return-object="false"
                      ></v-combobox>
                    </v-col>
                    <v-col>
                      <ValidationProvider
                        name="store_sale_url"
                        rules
                        v-slot="{ errors }"
                      >
                        <v-text-field
                          name="store_sale_url"
                          :label="$capitalize($tc('model.productbudget_ad_link'))"
                          prepend-inner-icon="mdi-link-variant"
                          :error-messages="errors"
                          v-model="budget_form.store_sale_url"
                          :disabled="submitting"
                        ></v-text-field>
                      </ValidationProvider>
                    </v-col>
                  </v-row>
                </v-card-text>
              </v-card>
              <ValidationProvider
                mode="lazy"
                name="price"
                :rules="{ required: true, min_value: 0.1 }"
                v-slot="{ errors }"
              >
                <vuetify-money
                  name="price"
                  type="text"
                  :options="money"
                  v-model="budget_form.price"
                  :error-messages="errors"
                  :label="$capitalize($tc('model.productbudget_price'))"
                  :disabled="submitting"
                />
              </ValidationProvider>
              <ScopeProvider scope="leads.damages.budgets.approve">
                <v-checkbox
                  v-model="budget_form.selected"
                  :label="$capitalize($tc('model.productbudget_approved_budget'))"
                  :disabled="submitting"
                ></v-checkbox>
              </ScopeProvider>
            </v-card-text>
            <v-divider></v-divider>
            <v-card-actions>
              <v-spacer></v-spacer>
              <v-btn @click="handleCloseBudgetDialog()" text>{{ $capitalize($tc("model.productbudget_cancel_button")) }}</v-btn>
              <v-btn type="submit" color="primary" :loading="submitting">
                {{ $capitalize($tc("model.productbudget_submit")) }}
              </v-btn>
            </v-card-actions>
          </ValidationObserver>
        </v-form>
      </v-card>
    </v-dialog>
    <confirm-action-dialog
      :confirm_action="confirmation_dialog"
      @change="confirmation_dialog = $event"
      @action="deleteBudget()"
      :message="$capitalize($tc('model.productbudget_budget_deleted'))"
    />
    <v-dialog
      v-model="marketplace_dialog"
      fullscreen
      hide-overlay
      transition="dialog-bottom-transition"
      scrollable
      height="100vh"
    >
      <marketplace
        @cancel="marketplace_dialog = false"
        :product="claim.product_feature"
        @budget_selected="selectBudget"
      />
    </v-dialog>
  </div>
</template>

<script>
import Money from "@/tools/money";
import { index as indexMarketplaces } from "@/services/marketplaces";
import {
  indexDamageBudgets,
  storeProductBudget,
  updateProductBudget,
  deleteProductBudget,
  approveProductBudget
} from "@/services/budgets";
import Marketplace from "@/components/products/Marketplace";
import ConfirmActionDialog from "@/components/ConfirmActionDialog";
import ScopeProvider from "@/components/ScopeProvider";

export default {
  components: { Marketplace, ConfirmActionDialog, ScopeProvider },
  name: "product-card",
  data: () => ({
    budget_dialog: false,
    marketplace_dialog: false,
    budget_form: {},
    money: {
      prefix: "€ ",
      suffix: "",
      precision: 2,
      length: 11,
      locale: "pt-PT"
    },
    submitting: false,
    delete_budget_id: null,
    confirmation_dialog: false,
    marketplace_dialog: false
  }),
  props: ["claim", "coverages"],
  methods: {
    updateProductBudgets(budgets) {
      this.$store.dispatch("damage_claim/updateBudgetsDamageClaim", {
        ...this.claim,
        budgets
      });
    },
    selectBudget(budget) {
      this.budget_form = {
        ...this.budget_form,
        store_sale_url: budget.url,
        store_name: budget.store,
        price: budget.price
      };
      this.marketplace_dialog = false;
    },
    handleConfirmBudgetExclusion(budget) {
      this.delete_budget_id = budget.id;
      this.confirmation_dialog = true;
    },
    async deleteBudget() {
      try {
        await deleteProductBudget(this.claim.id, this.delete_budget_id);
        this.confirmation_dialog = false;
        this.updateProductBudgets(
          this.claim.product_budgets.filter(
            budget => budget.id != this.delete_budget_id
          )
        );
      } catch (error) {
        this.$store.commit("sendMessage", {
          text: "Falha ao excluir orçamento",
          color: "red"
        });
      }
    },
    formatEuro(value) {
      return Money.euro(value ? value : 0);
    },
    calcTotalBudget() {
      return this.$listeners.calcBudget(this.claim);
    },
    handleEditBudget(budget) {
      this.budget_form = { ...budget };
      this.handleOpenBudgetDialog();
    },
    handleOpenBudgetDialog() {
      this.budget_dialog = true;
    },
    handleCloseBudgetDialog() {
      this.budget_form = {};
      this.$refs.form.reset();
      this.budget_dialog = false;
    },
    handleCloseMarketplaceDialog() {
      this.marketplace_dialog = false;
    },
    bindProductSale(sale) {
      this.budget_form = {
        ...this.budget_form,
        store_sale_url: sale.url,
        store_name: this.getTitleFromMarketplaceId(sale.marketplace),
        price: sale.price + ""
      };
      this.marketplace_dialog = false;
    },
    getTitleFromMarketplaceId(id) {
      const marketplace = this.getMarketplaceFromId(id);
      return marketplace.name;
    },
    getMarketplaceFromId(id) {
      return this.getMarketplaces().find(m => m.id === id);
    },
    getMarketplaces() {
      return indexMarketplaces();
    },
    async handleSubmit() {
      const validation = await this.$refs.form.validate();
      if (!validation) return;

      this.submitting = true;
      let response = [];
      try {
        if (this.budget_form.id) {
          response = await updateProductBudget(
            this.claim.id,
            this.budget_form.id,
            this.budget_form
          );
        } else {
          response = await storeProductBudget(this.claim.id, this.budget_form);
        }
        this.updateProductBudgets(response);
        this.handleCloseBudgetDialog();
        this.$emit("submit");
      } catch (error) {
        this.$store.commit("sendMessage", {
          text: "Falha ao salvar orçamento",
          color: "red"
        });
      } finally {
        this.submitting = false;
      }
    },
    async handleSelectProductBudget(value, budget) {
      try {
        budget.selected = value;
        const response = await approveProductBudget(this.claim.id, budget.id, {
          selected: value
        });
        this.updateProductBudgets(response);
        this.$emit("submit");
      } catch (error) {
        budget.selected = !value;
      }
    }
  },
  computed: {
    budgets: function() {
      return this.claim.product_budgets ? this.claim.product_budgets : [];
    }
  }
};
</script>
